// VFX Effects Configuration
// Configuration for 10 VFX effects with 3 intensity levels (light, medium, heavy)

import type { ISourceOptions } from '@tsparticles/engine';
import type { VFXType, VFXIntensity } from '../types/vfxConfig';

// Helper function to calculate particle count based on intensity
function getParticleCount(baseCount: number, intensity: VFXIntensity): number {
  switch (intensity) {
    case 'light':
      return Math.floor(baseCount * 0.5);
    case 'medium':
      return baseCount;
    case 'heavy':
      return Math.floor(baseCount * 1.5);
    default:
      return baseCount;
  }
}

// Base particle count for each effect
const BASE_PARTICLE_COUNTS: Record<VFXType, number> = {
  snow: 100,
  confetti: 50,
  fireworks: 30,
  bubbles: 27, // Reduced to 1/3 of original (80 -> 27)
  stars: 100,
  'particles_connect': 80,
  absorbers: 60,
  polygons: 50,
  emitters: 40,
  sparkles: 120,
};

/**
 * Get VFX configuration for a specific effect type and intensity
 */
export async function getVFXConfig(
  type: VFXType,
  intensity: VFXIntensity
): Promise<ISourceOptions> {
  const particleCount = getParticleCount(BASE_PARTICLE_COUNTS[type], intensity);

  switch (type) {
    case 'snow':
      return getSnowConfig(particleCount);
    case 'confetti':
      return getConfettiConfig(particleCount);
    case 'fireworks':
      return getFireworksConfig(particleCount);
    case 'bubbles':
      return getBubblesConfig(particleCount);
    case 'stars':
      return getStarsConfig(particleCount);
    case 'particles_connect':
      return getParticlesConnectConfig(particleCount);
    case 'absorbers':
      return getAbsorbersConfig(particleCount);
    case 'polygons':
      return getPolygonsConfig(particleCount);
    case 'emitters':
      return getEmittersConfig(particleCount);
    case 'sparkles':
      return getSparklesConfig(particleCount);
    default:
      return getSnowConfig(particleCount);
  }
}

// Snow Effect Configuration
function getSnowConfig(particleCount: number): ISourceOptions {
  // Simple snowflake SVG data URI - 6-pointed star shape
  const snowflakeSvg = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Cpath d='M50 0 L55 20 L75 20 L60 35 L65 55 L50 45 L35 55 L40 35 L25 20 L45 20 Z' fill='white'/%3E%3Cpath d='M50 100 L45 80 L25 80 L40 65 L35 45 L50 55 L65 45 L60 65 L75 80 L55 80 Z' fill='white'/%3E%3Cpath d='M0 50 L20 45 L20 25 L35 40 L55 35 L45 50 L55 65 L35 60 L20 75 L20 55 Z' fill='white'/%3E%3Cpath d='M100 50 L80 55 L80 75 L65 60 L45 65 L55 50 L45 35 L65 40 L80 25 L80 45 Z' fill='white'/%3E%3C/svg%3E";

  return {
    particles: {
      number: {
        value: particleCount,
      },
      color: {
        value: '#ffffff',
      },
      shape: {
        type: 'image',
        options: {
          image: {
            src: snowflakeSvg,
            width: 100,
            height: 100,
          },
        },
      },
      opacity: {
        value: 0.8,
      },
      size: {
        value: {
          min: 4,
          max: 8,
        },
      },
      rotate: {
        value: {
          min: 0,
          max: 360,
        },
        animation: {
          enable: true,
          speed: 1,
        },
      },
      move: {
        enable: true,
        speed: {
          min: 0.5,
          max: 1.5,
        },
        direction: 'bottom',
        outModes: {
          default: 'out',
        },
        gravity: {
          enable: false, // Disable gravity to prevent speed accumulation
        },
      },
    },
    background: {
      color: {
        value: 'transparent',
      },
    },
  };
}

// Confetti Effect Configuration
function getConfettiConfig(particleCount: number): ISourceOptions {
  return {
    particles: {
      number: {
        value: particleCount,
      },
      color: {
        value: ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff'],
      },
      shape: {
        type: ['circle', 'square'],
      },
      opacity: {
        value: 0.8,
      },
      size: {
        value: 5,
      },
      rotate: {
        value: {
          min: 0,
          max: 360,
        },
        animation: {
          enable: true,
          speed: 5,
        },
      },
      move: {
        enable: true,
        speed: {
          min: 1.5,
          max: 2.5,
        },
        direction: 'bottom',
        outModes: {
          default: 'out',
        },
        gravity: {
          enable: false, // Disable gravity to prevent speed accumulation
        },
      },
    },
    background: {
      color: {
        value: 'transparent',
      },
    },
  };
}

// Fireworks Effect Configuration
function getFireworksConfig(particleCount: number): ISourceOptions {
  return {
    particles: {
      number: {
        value: 0, // Start with 0, emitters will create particles
      },
      color: {
        value: ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff'],
      },
      shape: {
        type: 'circle',
      },
      opacity: {
        value: 1,
        animation: {
          enable: true,
          speed: 0.5,
          sync: false,
        },
      },
      size: {
        value: {
          min: 2,
          max: 5,
        },
      },
      move: {
        enable: true,
        speed: {
          min: 5,
          max: 15,
        },
        direction: 'none',
        outModes: {
          default: 'destroy',
        },
        gravity: {
          enable: true,
        },
      },
    },
    emitters: [
      {
        position: {
          x: 20,
          y: 20,
        },
        rate: {
          quantity: 10,
          delay: 0.3,
        },
        life: {
          count: 0,
          delay: 0.4,
          duration: 0.1,
        },
      },
      {
        position: {
          x: 80,
          y: 20,
        },
        rate: {
          quantity: 10,
          delay: 0.3,
        },
        life: {
          count: 0,
          delay: 0.4,
          duration: 0.1,
        },
      },
      {
        position: {
          x: 50,
          y: 30,
        },
        rate: {
          quantity: 10,
          delay: 0.3,
        },
        life: {
          count: 0,
          delay: 0.4,
          duration: 0.1,
        },
      },
    ],
    background: {
      color: {
        value: 'transparent',
      },
    },
  };
}

// Bubbles Effect Configuration - Option 2: Colorful
function getBubblesConfig(particleCount: number): ISourceOptions {
  return {
    particles: {
      number: {
        value: particleCount,
      },
      color: {
        value: [
          '#ff6b6b', '#4ecdc4', '#45b7d1', '#f9ca24', '#f0932b',
          '#eb4d4b', '#6c5ce7', '#a29bfe', '#fd79a8', '#fdcb6e'
        ],
      },
      shape: {
        type: 'circle',
      },
      opacity: {
        value: {
          min: 0.4,
          max: 0.8,
        },
        animation: {
          enable: true,
          speed: 1.5,
          sync: false,
        },
      },
      size: {
        value: {
          min: 15,
          max: 35,
        },
        animation: {
          enable: true,
          speed: 3,
          sync: false,
        },
      },
      stroke: {
        width: 2,
        color: {
          value: '#ffffff',
        },
        opacity: 0.6,
      },
      move: {
        enable: true,
        speed: {
          min: 0.8,
          max: 2.5,
        },
        direction: 'top',
        outModes: {
          default: 'out',
        },
        attract: {
          enable: true,
          rotate: {
            x: 600,
            y: 1200,
          },
        },
      },
    },
    background: {
      color: {
        value: 'transparent',
      },
    },
  };
}

// Stars Effect Configuration
function getStarsConfig(particleCount: number): ISourceOptions {
  return {
    particles: {
      number: {
        value: particleCount,
      },
      color: {
        value: [
          '#ffffff', // White
          '#ffd700', // Gold
          '#ffa500', // Orange
          '#ff6b9d', // Pink
          '#4ecdc4', // Cyan
          '#45b7d1', // Light Blue
          '#a29bfe', // Purple
          '#f9ca24', // Yellow
          '#6c5ce7', // Violet
          '#00d2d3', // Turquoise
        ],
      },
      shape: {
        type: 'star',
      },
      opacity: {
        value: {
          min: 0.3,
          max: 1,
        },
        animation: {
          enable: true,
          speed: 1,
          sync: false,
        },
      },
      size: {
        value: {
          min: 1,
          max: 4,
        },
      },
      move: {
        enable: true,
        speed: 0.5,
        direction: 'none',
        outModes: {
          default: 'out',
        },
      },
      twinkle: {
        particles: {
          enable: true,
          frequency: 0.05,
          opacity: 1,
        },
      },
    },
    background: {
      color: {
        value: 'transparent',
      },
    },
  };
}

// Particles Connect Effect Configuration
function getParticlesConnectConfig(particleCount: number): ISourceOptions {
  return {
    particles: {
      number: {
        value: particleCount,
      },
      color: {
        value: '#ffffff',
      },
      shape: {
        type: 'circle',
      },
      opacity: {
        value: 0.5,
      },
      size: {
        value: 3,
      },
      move: {
        enable: true,
        speed: 1,
        direction: 'none',
        outModes: {
          default: 'out',
        },
      },
      links: {
        enable: true,
        distance: 150,
        color: '#ffffff',
        opacity: 0.2,
        width: 1,
      },
    },
    background: {
      color: {
        value: 'transparent',
      },
    },
  };
}

// Absorbers Effect Configuration
function getAbsorbersConfig(particleCount: number): ISourceOptions {
  return {
    particles: {
      number: {
        value: particleCount,
      },
      color: {
        value: [
          '#ffffff',
          '#4ecdc4',
          '#45b7d1',
          '#f9ca24',
          '#ff6b6b',
        ],
      },
      shape: {
        type: 'circle',
      },
      opacity: {
        value: 0.8,
      },
      size: {
        value: {
          min: 3,
          max: 6,
        },
      },
      move: {
        enable: true,
        speed: {
          min: 1,
          max: 3,
        },
        direction: 'none',
        outModes: {
          default: 'out',
        },
      },
    },
    absorbers: [
      {
        position: {
          x: 20,
          y: 20,
        },
        size: {
          value: {
            min: 5,
            max: 12,
          },
          animation: {
            enable: true,
            speed: 0.5,
            sync: false,
            destroy: false,
          },
        },
        color: {
          value: '#ff6b6b',
        },
        opacity: 0.6,
        destroy: false,
        orbits: false,
      },
      {
        position: {
          x: 80,
          y: 20,
        },
        size: {
          value: {
            min: 5,
            max: 12,
          },
          animation: {
            enable: true,
            speed: 0.5,
            sync: false,
            destroy: false,
          },
        },
        color: {
          value: '#4ecdc4',
        },
        opacity: 0.6,
        destroy: false,
        orbits: false,
      },
      {
        position: {
          x: 20,
          y: 80,
        },
        size: {
          value: {
            min: 5,
            max: 12,
          },
          animation: {
            enable: true,
            speed: 0.5,
            sync: false,
            destroy: false,
          },
        },
        color: {
          value: '#f9ca24',
        },
        opacity: 0.6,
        destroy: false,
        orbits: false,
      },
      {
        position: {
          x: 80,
          y: 80,
        },
        size: {
          value: {
            min: 5,
            max: 12,
          },
          animation: {
            enable: true,
            speed: 0.5,
            sync: false,
            destroy: false,
          },
        },
        color: {
          value: '#45b7d1',
        },
        opacity: 0.6,
        destroy: false,
        orbits: false,
      },
    ],
    background: {
      color: {
        value: 'transparent',
      },
    },
  };
}

// Polygons Effect Configuration
function getPolygonsConfig(particleCount: number): ISourceOptions {
  return {
    particles: {
      number: {
        value: particleCount,
      },
      color: {
        value: [
          '#ff6b6b', // Red
          '#4ecdc4', // Cyan
          '#45b7d1', // Light Blue
          '#f9ca24', // Yellow
          '#6c5ce7', // Violet
          '#a29bfe', // Purple
          '#00d2d3', // Turquoise
          '#ffa500', // Orange
          '#ff6b9d', // Pink
          '#00ff00', // Green
        ],
      },
      shape: {
        type: 'polygon',
        options: {
          polygon: {
            sides: {
              min: 3,
              max: 8,
            },
          },
        },
      },
      opacity: {
        value: {
          min: 0.3,
          max: 0.9,
        },
        animation: {
          enable: true,
          speed: 1.5,
          sync: false,
        },
      },
      size: {
        value: {
          min: 4,
          max: 8,
        },
        animation: {
          enable: true,
          speed: 2,
          sync: false,
        },
      },
      rotate: {
        value: {
          min: 0,
          max: 360,
        },
        animation: {
          enable: true,
          speed: {
            min: 1,
            max: 3,
          },
        },
      },
      move: {
        enable: true,
        speed: {
          min: 0.5,
          max: 2,
        },
        direction: 'none',
        outModes: {
          default: 'out',
        },
      },
    },
    background: {
      color: {
        value: 'transparent',
      },
    },
  };
}

// Emitters Effect Configuration
function getEmittersConfig(particleCount: number): ISourceOptions {
  return {
    particles: {
      number: {
        value: 0, // Start with 0, emitters will create particles
        limit: {
          value: 20, // Maximum 20 particles on screen at once
          mode: 'delete', // Delete oldest particles when limit is reached
        },
      },
      color: {
        value: ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff'],
      },
      shape: {
        type: 'circle',
      },
      opacity: {
        value: 0.8,
      },
      size: {
        value: {
          min: 3,
          max: 6,
        },
      },
      move: {
        enable: true,
        speed: {
          min: 1,
          max: 3,
        },
        direction: 'none',
        outModes: {
          default: 'destroy', // Destroy particles when they go out of bounds
        },
      },
      life: {
        duration: {
          value: 3, // Particles will disappear after 3 seconds
        },
      },
    },
    emitters: [
      {
        position: {
          x: 25,
          y: 25,
        },
        rate: {
          quantity: 1,
          delay: 1,
        },
        life: {
          count: 0, // Infinite
          delay: 0,
          duration: 0.1,
        },
      },
      {
        position: {
          x: 75,
          y: 75,
        },
        rate: {
          quantity: 1,
          delay: 1,
        },
        life: {
          count: 0, // Infinite
          delay: 0,
          duration: 0.1,
        },
      },
    ],
    background: {
      color: {
        value: 'transparent',
      },
    },
  };
}

// Sparkles Effect Configuration
// Sparkles are small, twinkling light points that shimmer and fade
function getSparklesConfig(particleCount: number): ISourceOptions {
  return {
    particles: {
      number: {
        value: particleCount,
      },
      color: {
        value: [
          '#ffffff', // White
          '#ffd700', // Gold
          '#ffa500', // Orange
          '#ffff00', // Yellow
          '#ff6b9d', // Pink
          '#4ecdc4', // Cyan
          '#45b7d1', // Light Blue
          '#a29bfe', // Purple
        ],
      },
      shape: {
        type: 'circle', // Circle shape for sparkles (small light points)
      },
      opacity: {
        value: {
          min: 0.3,
          max: 1,
        },
        animation: {
          enable: true,
          speed: 2.5,
          sync: false,
        },
      },
      size: {
        value: {
          min: 1,
          max: 4,
        },
        animation: {
          enable: true,
          speed: 3,
          sync: false,
        },
      },
      move: {
        enable: true,
        speed: {
          min: 0.5,
          max: 1.5,
        },
        direction: 'none',
        outModes: {
          default: 'out',
        },
      },
      twinkle: {
        particles: {
          enable: true,
          frequency: 0.15,
          opacity: 1,
        },
      },
    },
    background: {
      color: {
        value: 'transparent',
      },
    },
  };
}


